import { useState, useEffect } from 'react';
import {
  Gift, CheckCircle, DollarSign, Filter, Calendar, Search, Clock,
  ChevronDown, X, ArrowUpDown, Loader2, BarChart3, Percent,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

function getDateString(date) {
  return date.toISOString().split('T')[0];
}

function getQuickFilterRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const yesterday = new Date(today.getTime() - 864e5);
  const ranges = {
    today:      [today, today],
    yesterday:  [yesterday, yesterday],
    last7:      [new Date(today.getTime() - 6 * 864e5), today],
    last30:     [new Date(today.getTime() - 29 * 864e5), today],
    last90:     [new Date(today.getTime() - 89 * 864e5), today],
    thisweek:   [startOfWeek, today],
    thismonth:  [startOfMonth, today],
    thisquarter:[startOfQuarter, today],
    ytd:        [startOfYear, today],
    lastyear:   [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
  };
  const [s, e] = ranges[key] || ranges.last30;
  return { start: getDateString(s), end: getDateString(e) };
}

const QUICK_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: 'last90', label: 'Last 90 Days' },
  { key: 'thisweek', label: 'This Week' },
  { key: 'thismonth', label: 'This Month' },
  { key: 'thisquarter', label: 'This Quarter' },
  { key: 'ytd', label: 'YTD' },
  { key: 'lastyear', label: 'Last Year' },
];

const DiscountReport = () => {
  const defaultRange = getQuickFilterRange('last30');
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [quickFilter, setQuickFilter] = useState('last30');
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'redeemed', dir: 'desc' });
  const [nameSearch, setNameSearch] = useState('');
  const [useBusinessHours, setUseBusinessHours] = useState(true);
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('06:00');
  const [breakout, setBreakout] = useState({ open: false, row: null });
  const [breakoutData, setBreakoutData] = useState(null);
  const [breakoutLoading, setBreakoutLoading] = useState(false);

  useEffect(() => { fetchReport(); }, []);

  function applyQuickFilter(key) {
    const { start, end } = getQuickFilterRange(key);
    setStartDate(start);
    setEndDate(end);
    setQuickFilter(key);
  }

  function handleDateChange(field, value) {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickFilter(null);
  }

  function toggleReward(reward) {
    setSelectedRewards(prev =>
      prev.some(r => r.id === reward.id)
        ? prev.filter(r => r.id !== reward.id)
        : [...prev, reward]
    );
  }

  async function fetchReport() {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedRewards.length > 0) {
        params.reward_ids = selectedRewards.map(r => r.id).join(',');
      }
      if (useBusinessHours) {
        params.start_time = startTime;
        params.end_time = endTime;
      }
      const res = await api.get('/admin/reports/discounts', { params });
      setData(res.data);
      setAvailableRewards(res.data.rewards || []);
    } catch (err) {
      console.error('Failed to fetch discount report:', err);
      toast.error('Failed to load discount report');
    } finally {
      setLoading(false);
    }
  }

  async function openBreakout(row) {
    setBreakout({ open: true, row });
    setBreakoutData(null);
    setBreakoutLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate, reward_name: row.rewardName };
      if (useBusinessHours) {
        params.start_time = startTime;
        params.end_time = endTime;
      }
      const res = await api.get('/admin/reports/discounts/redemptions', { params });
      setBreakoutData(res.data);
    } catch (err) {
      console.error('Failed to fetch reward redemptions:', err);
      toast.error('Failed to load player breakout');
    } finally {
      setBreakoutLoading(false);
    }
  }

  function closeBreakout() {
    setBreakout({ open: false, row: null });
    setBreakoutData(null);
  }

  function handleSort(key) {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  }

  const filteredRewards = availableRewards.filter(r =>
    !nameSearch || r.name.toLowerCase().includes(nameSearch.toLowerCase())
  );

  function getSortedData() {
    if (!data?.breakdown) return [];
    const filtered = nameSearch
      ? data.breakdown.filter(row => row.rewardName.toLowerCase().includes(nameSearch.toLowerCase()))
      : data.breakdown;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') {
        return sortConfig.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortConfig.dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  const sorted = getSortedData();
  const fmt = (v) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  // Compute summary from filtered breakdown so cards match the visible table
  const filteredSummary = (() => {
    if (!sorted.length) return data?.summary || { totalDistributed: 0, totalDistributedValue: 0, totalRedeemed: 0, totalValue: 0, redemptionRate: 0 };
    const totalDistributed = sorted.reduce((s, r) => s + r.distributed, 0);
    const totalDistributedValue = sorted.reduce((s, r) => s + (r.distributedValue || 0), 0);
    const totalRedeemed = sorted.reduce((s, r) => s + r.redeemed, 0);
    const totalValue = sorted.reduce((s, r) => s + r.totalValue, 0);
    const redemptionRate = totalDistributed > 0 ? (totalRedeemed / totalDistributed * 100) : 0;
    return { totalDistributed, totalDistributedValue, totalRedeemed, totalValue, redemptionRate };
  })();

  return (
    <div className="space-y-6">
      {/* Date subtitle */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <BarChart3 size={16} className="text-amber-500" />
        <span>{startDate} &mdash; {endDate}</span>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-5 space-y-5">
        {/* Name search */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            <Search size={14} className="text-amber-500" /> Search by Name
          </label>
          <input
            type="text"
            value={nameSearch}
            onChange={e => setNameSearch(e.target.value)}
            placeholder="Type to filter rewards (e.g. rainy)"
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
          />
        </div>

        {/* Reward selector */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            <Filter size={14} className="text-amber-500" /> Rewards
          </label>
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg flex items-center justify-between text-gray-300 hover:border-gray-500 transition-colors"
            >
              {selectedRewards.length === 0
                ? 'All Rewards'
                : `${selectedRewards.length} reward${selectedRewards.length !== 1 ? 's' : ''} selected`}
              <ChevronDown size={16} />
            </button>
            {showDropdown && (
              <div className="absolute top-full mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg shadow-xl z-10 max-h-52 overflow-y-auto">
                {filteredRewards.map(r => (
                  <label key={r.id} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-600 cursor-pointer text-white text-sm">
                    <input
                      type="checkbox"
                      checked={selectedRewards.some(s => s.id === r.id)}
                      onChange={() => toggleReward(r)}
                      className="w-4 h-4 rounded accent-amber-500"
                    />
                    {r.name}
                  </label>
                ))}
                {filteredRewards.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">{nameSearch ? 'No matching rewards' : 'No rewards found'}</p>
                )}
              </div>
            )}
          </div>
          {selectedRewards.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedRewards.map(r => (
                <span key={r.id} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-500 text-black">
                  {r.name}
                  <button onClick={() => toggleReward(r)} className="hover:opacity-70"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Quick filters */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            <Calendar size={14} className="text-amber-500" /> Quick Filters
          </label>
          <div className="flex flex-wrap gap-2">
            {QUICK_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => applyQuickFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  quickFilter === f.key
                    ? 'bg-amber-500 text-black border-amber-500'
                    : 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date range + Apply */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">Start Date</label>
            <input type="date" value={startDate}
              onChange={e => handleDateChange('start', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm [color-scheme:dark]"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1">End Date</label>
            <input type="date" value={endDate}
              onChange={e => handleDateChange('end', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm [color-scheme:dark]"
            />
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="px-6 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
          </button>
        </div>

        {/* Business Hours Filter */}
        <div className="border-t border-gray-700 pt-5">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
              <Clock size={14} className="text-amber-500" /> Business Hours Filter
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useBusinessHours}
                onChange={e => setUseBusinessHours(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500 accent-amber-500"
              />
              <span className="text-sm text-gray-400">Filter by time</span>
            </label>
          </div>
          {useBusinessHours && (
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 [color-scheme:dark]"
                />
              </div>
              <div className="text-gray-500 pt-5">to</div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 [color-scheme:dark]"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 size={32} className="animate-spin text-amber-500" />
        </div>
      )}

      {/* Summary Stats */}
      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {[
              { label: 'Total Distributed', value: filteredSummary.totalDistributed.toLocaleString(), icon: Gift },
              { label: 'Distributed Value', value: fmt(filteredSummary.totalDistributedValue), icon: DollarSign },
              { label: 'Total Redeemed', value: filteredSummary.totalRedeemed.toLocaleString(), icon: CheckCircle },
              { label: 'Redeemed Value', value: fmt(filteredSummary.totalValue), icon: DollarSign },
              { label: 'Redemption Rate', value: `${filteredSummary.redemptionRate.toFixed(1)}%`, icon: Percent },
            ].map(stat => (
              <div key={stat.label} className="bg-gray-800 border border-gray-700 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-400">{stat.label}</span>
                  <stat.icon size={18} className="text-amber-500" />
                </div>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            {sorted.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-750 border-b border-gray-700">
                      {[
                        { key: 'rewardName', label: 'Reward Name' },
                        { key: 'distributed', label: 'Distributed' },
                        { key: 'distributedValue', label: 'Dist. Value' },
                        { key: 'redeemed', label: 'Redeemed' },
                        { key: 'redemptionRate', label: 'Rate (%)' },
                        { key: 'totalValue', label: 'Redeemed Value' },
                      ].map(col => (
                        <th key={col.key} className="px-5 py-3 text-left">
                          <button
                            onClick={() => handleSort(col.key)}
                            className="flex items-center gap-1.5 text-gray-300 hover:text-amber-400 font-semibold transition-colors"
                          >
                            {col.label}
                            {sortConfig.key === col.key && <ArrowUpDown size={13} />}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => (
                      <tr
                        key={row.rewardId}
                        onClick={() => openBreakout(row)}
                        title="Click to see who redeemed this reward"
                        className={`border-b border-gray-700/50 cursor-pointer hover:bg-amber-500/10 transition-colors ${i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/60'}`}
                      >
                        <td className="px-5 py-3 text-white font-medium">{row.rewardName}</td>
                        <td className="px-5 py-3 text-white">{row.distributed.toLocaleString()}</td>
                        <td className="px-5 py-3 text-white">{fmt(row.distributedValue || 0)}</td>
                        <td className="px-5 py-3 text-white">{row.redeemed.toLocaleString()}</td>
                        <td className="px-5 py-3">
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400">
                            {row.redemptionRate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-5 py-3 text-white">{fmt(row.totalValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <BarChart3 size={40} className="mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No data found for the selected filters</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Per-reward player breakout modal */}
      {breakout.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={closeBreakout}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-white">{breakout.row?.rewardName}</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Players who redeemed {'·'} {startDate} &mdash; {endDate}
                  {useBusinessHours && ` · ${startTime}–${endTime}`}
                </p>
              </div>
              <button onClick={closeBreakout} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-5">
              {breakoutLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 size={28} className="animate-spin text-amber-500" />
                </div>
              ) : breakoutData?.redemptions?.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-400">
                      {breakoutData.count} redemption{breakoutData.count !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-gray-400">
                      Total: <span className="text-white font-semibold">
                        {fmt(breakoutData.redemptions.reduce((s, r) => s + (r.value || 0), 0))}
                      </span>
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-750 border-b border-gray-700 text-left text-gray-300">
                          <th className="px-4 py-2 font-semibold">Player</th>
                          <th className="px-4 py-2 font-semibold">Redeemed</th>
                          <th className="px-4 py-2 font-semibold">Staff</th>
                          <th className="px-4 py-2 font-semibold text-right">Buy-In</th>
                          <th className="px-4 py-2 font-semibold text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakoutData.redemptions.map((r, i) => (
                          <tr key={r.id} className={`border-b border-gray-700/50 ${i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/60'}`}>
                            <td className="px-4 py-2 text-white">
                              <div className="font-medium">{r.playerName}</div>
                              {r.email && <div className="text-xs text-gray-500">{r.email}</div>}
                            </td>
                            <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                              {new Date(r.redeemedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </td>
                            <td className="px-4 py-2 text-gray-300">
                              {r.staffName ? `${r.staffName}${r.staffNumber ? ` (#${r.staffNumber})` : ''}` : <span className="text-gray-600">—</span>}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-300">
                              {r.buyInAmount != null ? fmt(r.buyInAmount) : <span className="text-gray-600">—</span>}
                            </td>
                            <td className="px-4 py-2 text-right text-white font-medium">{fmt(r.value || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="py-12 text-center">
                  <BarChart3 size={36} className="mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400">No redemptions found for this reward in the selected range</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountReport;
